import { Dialog, Transition } from "@headlessui/react";
import { PencilIcon, PlusIcon, XIcon } from "@heroicons/react/solid";
import { isEmpty } from "lodash";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCommissionReports } from ".";
import ContactComponent from "../../components/ContactComponent";
import { getLabelColorStyle } from "../../components/labels/utils";
import { renderDate } from "../../pages/Map/HotspotDetails/HotspotInfoTab";
import { getCommissionReportPeriodText } from "../../pages/Workspace/CommissionReportsData/CommissionReports";
import PaymentMethodComponent from "../../pages/Workspace/common/PaymentMethodComponent";
import PaymentMethodFormSidebar, {
  EditPaymentMethodConfig
} from "../../pages/Workspace/ContactsData/PaymentMethodFormSidebar";
import classNames from "../../utils/classNames";
import { formatDateToLocale } from "../../utils/dates";
import {
  CommissionPaymentStatus,
  CommissionReport,
  ContactCommissions,
  PaymentMethod
} from "../../utils/types";
import useCurrency, { CurrencyKey } from "../../utils/useCurrency";
import { useHntMarket } from "../../utils/useHntMarket";
import { useAppearance } from "../appearance";
import { useContacts } from "../contacts";
import { getSmartLabelColor } from "../filteredList/FilteredListProvider";
import ContactCommissionsList from "./ContactCommissionsList";

interface Props {}

const ContactCommissionsSummarySidebar: React.FC<Props> = () => {
  const dialogRef = useRef(null);
  const { contacts, addPaymentMethod } = useContacts();
  const { isDark } = useAppearance();
  const { getCurrencyTitle, currencyDisplay } = useCurrency();
  const { displayInCurrency } = useHntMarket();
  const {
    contactCommissionsSummarySidebarConfig,
    setContactCommissionsSummarySidebarConfig,
    commissionPaymentSummarySidebarConfig,
    hntPaymentSidebarConfig,
    setHntPaymentSidebarConfig,
    setCommissionPaymentSummarySidebarConfig
  } = useCommissionReports();
  const [editPaymentMethodConfig, setEditPaymentMethodConfig] =
    useState<EditPaymentMethodConfig>({ show: false });

  const closeSidebar = useCallback(
    (isCloseAll: boolean) => {
      setContactCommissionsSummarySidebarConfig({ show: false });

      if (!isCloseAll) return;

      if (commissionPaymentSummarySidebarConfig.show) {
        setCommissionPaymentSummarySidebarConfig({ show: false });
      }

      if (hntPaymentSidebarConfig.show) {
        setHntPaymentSidebarConfig({ show: false });
      }
    },
    [
      hntPaymentSidebarConfig.show,
      commissionPaymentSummarySidebarConfig.show,
      setContactCommissionsSummarySidebarConfig,
      setHntPaymentSidebarConfig,
      setCommissionPaymentSummarySidebarConfig
    ]
  );

  const { show, commissionReport, contactCommissions, getPaymentStatus } =
    useMemo(
      () => contactCommissionsSummarySidebarConfig,
      [contactCommissionsSummarySidebarConfig]
    );

  const currentContact = useMemo(() => {
    if (!contactCommissions) return null;

    return contacts[contactCommissions.contact.uuid];
  }, [contacts, contactCommissions]);

  const renderContent = useMemo(() => {
    if (!commissionReport || !contactCommissions || !getPaymentStatus) {
      return null;
    }

    const paymentStatus = getPaymentStatus(contactCommissions);

    return (
      <div className="flex-1 py-6 divide-y divide-gray-200 dark:divide-gray-500 overflow-y-auto">
        <div className="px-4 sm:px-6 space-y-6">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                Commission amount in period
              </dt>

              <dd className="flex items-baseline mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {commissionReport.currency === CurrencyKey.hnt
                  ? displayInCurrency(contactCommissions.totalCommissionValue, {
                      withHntSuffix: true,
                      needsDivision: true,
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3
                    })
                  : currencyDisplay(
                      commissionReport.currency,
                      contactCommissions.totalCommissionValue
                    )}
              </dd>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">
              Payment status
            </h3>
            <div className="mt-2">
              {paymentStatus.contactPayment && (
                <Link
                  onClick={() => closeSidebar(true)}
                  className="text-gray-900 dark:text-white cursor-pointer hover:text-gray-600 dark:hover:text-white hover:underline"
                  to={`/contacts/${contactCommissions.contact.uuid}/payments`}
                >{`Marked as paid on ${formatDateToLocale(
                  paymentStatus.contactPayment.created_at
                )}. Click to see more.`}</Link>
              )}

              {paymentStatus.paymentTransaction &&
                !paymentStatus.contactPayment && (
                  <Link
                    onClick={() => closeSidebar(true)}
                    className="text-gray-900 dark:text-white cursor-pointer hover:text-gray-600 dark:hover:text-white hover:underline"
                    to="/workspace/wallets"
                  >{`Paid on ${renderDate(
                    paymentStatus.paymentTransaction.time
                  )} by QR payment, confirmed on-chain. Click to see more.`}</Link>
                )}

              {paymentStatus.status === "unpaid" && "Unpaid"}

              {paymentStatus.status === "loading" && "Loading"}
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-medium text-gray-500">Contact</h3>
            {currentContact ? (
              <Link
                className="relative flex items-center space-x-3 group"
                to={`/contacts/${contactCommissions.contact.uuid}/payments`}
                onClick={() => closeSidebar(true)}
              >
                <ContactComponent
                  contactId={contactCommissions.contact.uuid}
                  fallbackName={contactCommissions.contact.name}
                />
              </Link>
            ) : (
              <div className="relative flex items-center space-x-3">
                <ContactComponent
                  contactId={contactCommissions.contact.uuid}
                  fallbackName={contactCommissions.contact.name}
                />
              </div>
            )}

            {currentContact && (
              <div className="mt-6">
                <div className="mb-4 flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-500">
                    Payment methods
                  </h3>
                  <div
                    onClick={() => {
                      setEditPaymentMethodConfig({
                        show: true,
                        contactLinkedUuid: currentContact.uuid,
                        initialCurrency: commissionReport.currency,
                        onSuccess: async (paymentMethod: PaymentMethod) => {
                          if (!currentContact) return;

                          await addPaymentMethod(paymentMethod, currentContact);
                        }
                      });
                    }}
                    className="flex items-center text-sm text-gray-500 font-medium dark:text-white cursor-pointer"
                  >
                    <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                    Add payment method
                  </div>
                </div>
                <div className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800 sm:rounded-lg">
                  {isEmpty(currentContact?.payment_methods) ? (
                    <div className="px-6 py-4">
                      No payment methods were added yet
                    </div>
                  ) : (
                    currentContact.payment_methods.map(
                      (paymentMethod, paymentIndex) => (
                        <div
                          key={paymentIndex}
                          className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group"
                        >
                          <div className="flex-1 flex-col min-w-0 focus:outline-none">
                            <PaymentMethodComponent
                              paymentMethod={paymentMethod}
                            />
                          </div>
                          <button
                            className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            onClick={() => {
                              setEditPaymentMethodConfig({
                                show: true,
                                contactLinkedUuid:
                                  contactCommissions.contact?.uuid,
                                parentForm: "contact",
                                paymentMethod
                              });
                            }}
                          >
                            <PencilIcon
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      )
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <ContactCommissionsList
              commissionReport={commissionReport}
              commissions={contactCommissions.commissions}
              closeSidebar={closeSidebar}
            />
          </div>
        </div>
      </div>
    );
  }, [
    addPaymentMethod,
    closeSidebar,
    commissionReport,
    contactCommissions,
    currencyDisplay,
    currentContact,
    displayInCurrency,
    getPaymentStatus
  ]);

  const renderSubtitle = useMemo(() => {
    if (!commissionReport || !contactCommissions || !getPaymentStatus) {
      return null;
    }

    const paymentStatus = getPaymentStatus(contactCommissions);

    return (
      <div className="flex items-center flex-wrap">
        <div
          className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2 mt-2"
          style={getLabelColorStyle("#7057ff", isDark)}
        >
          {getCommissionReportPeriodText(commissionReport)}
        </div>
        <div
          className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2 mt-2"
          style={getLabelColorStyle("#d73aaa", isDark)}
        >
          {getCurrencyTitle(commissionReport.currency)}
        </div>
        <div
          className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-2"
          style={getLabelColorStyle(
            getSmartLabelColor(paymentStatus.status),
            isDark
          )}
        >
          {paymentStatus.status}
        </div>
      </div>
    );
  }, [
    commissionReport,
    contactCommissions,
    getCurrencyTitle,
    getPaymentStatus,
    isDark
  ]);

  const checkCanShow = useCallback(() => {
    if (
      commissionPaymentSummarySidebarConfig.show ||
      hntPaymentSidebarConfig?.show ||
      editPaymentMethodConfig.show
    )
      return false;

    return true;
  }, [
    commissionPaymentSummarySidebarConfig.show,
    hntPaymentSidebarConfig?.show,
    editPaymentMethodConfig.show
  ]);

  return (
    <>
      <Transition.Root show={show} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-20 overflow-hidden"
          open={checkCanShow()}
          onClose={() => {
            if (editPaymentMethodConfig.show) return;

            closeSidebar(false);
          }}
          initialFocus={dialogRef}
        >
          <PaymentMethodFormSidebar
            config={editPaymentMethodConfig}
            setConfig={setEditPaymentMethodConfig}
          />
          <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div
              className={classNames(
                "fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500",
                editPaymentMethodConfig.show && "pr-40"
              )}
            >
              <div
                className="absolute inset-0"
                onClick={() => {
                  if (editPaymentMethodConfig.show) return;

                  closeSidebar(false);
                }}
              />
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="relative w-screen max-w-md dark:text-white">
                  <div className="max-w-3xl xl:max-w-6xl h-full">
                    <div className="h-full flex flex-col bg-white shadow-xl dark:bg-gray-900">
                      <div className="flex-1 h-0 flex flex-col">
                        <div className="flex-none py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              Contact commissions
                              {renderSubtitle}
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={() => closeSidebar(false)}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>
                        {renderContent}
                      </div>
                    </div>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ContactCommissionsSummarySidebar;

//
// Utils
//

export interface ContactCommissionsSummarySidebarConfig {
  show: boolean;
  commissionReport?: CommissionReport;
  contactCommissions?: ContactCommissions;
  parentSidebar?: "commission-payment-summary" | "hnt-payment";
  getPaymentStatus?: (
    contactCommissions: ContactCommissions
  ) => CommissionPaymentStatus;
}
